import { useCallback, useEffect, useRef, useState, forwardRef } from 'react'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import './VoiceTextarea.css'

type VoiceState = 'idle' | 'preparing' | 'ready' | 'recording' | 'finalizing' | 'error'

interface VoiceTextareaProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  rows?: number
  minHeight?: number
  maxHeight?: number
  classNamePrefix?: string
  label?: string
  compact?: boolean
}

const DG_WS_BASE = 'wss://api.deepgram.com/v1/listen'

const IconMic = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
)

const IconStop = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <rect x="5" y="5" width="14" height="14" rx="2" />
  </svg>
)

const IconLoader = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="vt-spin">
    <circle cx="12" cy="12" r="9" strokeOpacity="0.2" />
    <path d="M21 12a9 9 0 0 0-9-9" />
  </svg>
)

const VoiceTextarea = forwardRef<HTMLTextAreaElement, VoiceTextareaProps>(({
  value = '',
  onChange,
  placeholder = 'Clicca il microfono e parla... oppure digita manualmente',
  rows = 3,
  minHeight = 150,
  maxHeight = 300,
  classNamePrefix: p = 'voice-textarea',
  label = 'Campo',
  compact = false,
}, ref) => {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [interimText, setInterimText] = useState('')
  const [audioLevel, setAudioLevel] = useState(0)

  const voiceStateRef = useRef<VoiceState>('idle')
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  const wsRef = useRef<WebSocket | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const animFrameRef = useRef<number | null>(null)
  const finalizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryCountRef = useRef(0)
  const selectionRef = useRef<{ start: number; end: number } | null>(null)
  const internalTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  const setTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    internalTextareaRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }, [ref])

  useEffect(() => { valueRef.current = value }, [value])
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const setState = useCallback((s: VoiceState) => {
    voiceStateRef.current = s
    setVoiceState(s)
  }, [])

  const stopVisualizer = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = null
    }
    audioCtxRef.current?.close().catch(() => {})
    audioCtxRef.current = null
    setAudioLevel(0)
  }, [])

  const cleanup = useCallback(() => {
    stopVisualizer()
    if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = null
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    if (wsRef.current) {
      wsRef.current.onmessage = null
      wsRef.current.onerror = null
      wsRef.current.onclose = null
      if (wsRef.current.readyState <= WebSocket.OPEN) wsRef.current.close()
      wsRef.current = null
    }
    if (finalizeTimerRef.current) {
      clearTimeout(finalizeTimerRef.current)
      finalizeTimerRef.current = null
    }
    setInterimText('')
  }, [stopVisualizer])

  useEffect(() => () => cleanup(), [cleanup])

  const startVisualizer = useCallback((stream: MediaStream) => {
    try {
      const ctx = new AudioContext()
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      ctx.createMediaStreamSource(stream).connect(analyser)
      audioCtxRef.current = ctx
      const data = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        analyser.getByteFrequencyData(data)
        const avg = data.reduce((a, b) => a + b, 0) / data.length
        setAudioLevel(Math.min(100, avg * 2.5))
        animFrameRef.current = requestAnimationFrame(tick)
      }
      animFrameRef.current = requestAnimationFrame(tick)
    } catch { /* visualizer non critico */ }
  }, [])

  const showError = useCallback((msg: string) => {
    cleanup()
    setErrorMsg(msg)
    setState('error')
  }, [cleanup, setState])

  const startSession = useCallback(async () => {
    setState('preparing')
    setErrorMsg(null)
    setInterimText('')

    try {
      const [token, stream] = await Promise.all([
        axiosInstance.post<{ key: string }>(API_ENDPOINTS.SPEECH.DG_TOKEN).then(r => r.data.key),
        navigator.mediaDevices.getUserMedia({ audio: true }),
      ])

      streamRef.current = stream

      const url = `${DG_WS_BASE}?model=nova-3&language=it&smart_format=true&interim_results=true&punctuate=true&endpointing=300&vad_events=true`
      const ws = new WebSocket(url, ['token', token])
      wsRef.current = ws

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('WebSocket timeout')), 8000)
        ws.onopen = () => { clearTimeout(timeout); requestAnimationFrame(() => resolve()) }
        ws.onerror = () => { clearTimeout(timeout); reject(new Error('Connessione Deepgram fallita')) }
      })

      let hasFirstTranscript = false

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string)
          if (msg.type !== 'Results') return

          const transcript: string = msg.channel?.alternatives?.[0]?.transcript ?? ''
          if (!transcript) return

          if (!hasFirstTranscript) {
            hasFirstTranscript = true
            if (voiceStateRef.current === 'ready') {
              voiceStateRef.current = 'recording'
              setVoiceState('recording')
            }
          }

          if (msg.is_final) {
            setInterimText('')
            const trimmed = transcript.trim()
            if (trimmed) {
              const current = valueRef.current ?? ''
              let newValue: string
              const sel = selectionRef.current
              if (sel !== null) {
                const before = current.slice(0, sel.start)
                const after = current.slice(sel.end)
                const sep = before && !before.endsWith(' ') ? ' ' : ''
                newValue = before + sep + trimmed + after
                selectionRef.current = { start: before.length + sep.length + trimmed.length, end: before.length + sep.length + trimmed.length }
              } else {
                newValue = current ? current.trimEnd() + ' ' + trimmed : trimmed
              }
              onChangeRef.current?.(newValue)
            }
            if (voiceStateRef.current === 'finalizing') {
              clearTimeout(finalizeTimerRef.current!)
              finalizeTimerRef.current = null
              cleanup()
              voiceStateRef.current = 'idle'
              setVoiceState('idle')
            }
          } else {
            setInterimText(transcript)
          }
        } catch { /* ignora parse errors */ }
      }

      ws.onclose = () => {
        const s = voiceStateRef.current
        if (s === 'finalizing') {
          clearTimeout(finalizeTimerRef.current!)
          finalizeTimerRef.current = null
          cleanup()
          voiceStateRef.current = 'idle'
          setVoiceState('idle')
        } else if (s === 'ready' || s === 'recording') {
          showError('Connessione persa. Riprova.')
        }
      }

      ws.onerror = () => {
        const s = voiceStateRef.current
        if (s !== 'idle' && s !== 'error') showError('Errore connessione al servizio di dettatura.')
      }

      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg']
        .find(t => MediaRecorder.isTypeSupported(t)) || ''
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data)
      }

      recorder.start(250)
      retryCountRef.current = 0
      voiceStateRef.current = 'ready'
      setVoiceState('ready')
      startVisualizer(stream)

    } catch (err: any) {
      const isPermission = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
      const isConfig = err?.response?.status === 503

      if (isPermission) {
        showError('Permesso microfono negato. Abilitalo nelle impostazioni del browser.')
      } else if (isConfig) {
        showError('Servizio voce non configurato. Contatta l\'amministratore.')
      } else if (retryCountRef.current < 2) {
        retryCountRef.current++
        const delay = retryCountRef.current === 1 ? 500 : 1500
        voiceStateRef.current = 'idle'
        setVoiceState('idle')
        setTimeout(() => startSession(), delay)
      } else {
        showError(err?.message || 'Impossibile avviare la dettatura.')
      }
    }
  }, [cleanup, setState, showError, startVisualizer])

  const stopSession = useCallback(() => {
    voiceStateRef.current = 'finalizing'
    setVoiceState('finalizing')
    setInterimText('')
    stopVisualizer()

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'CloseStream' }))
    }
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop()
    }

    finalizeTimerRef.current = setTimeout(() => {
      cleanup()
      voiceStateRef.current = 'idle'
      setVoiceState('idle')
    }, 2000)
  }, [cleanup, stopVisualizer])

  const handleMicClick = () => {
    const s = voiceStateRef.current
    if (s === 'idle' || s === 'error') {
      const ta = internalTextareaRef.current
      selectionRef.current = ta && ta.selectionStart !== ta.selectionEnd
        ? { start: ta.selectionStart, end: ta.selectionEnd }
        : null
      startSession()
    } else if (s === 'ready' || s === 'recording') {
      stopSession()
    }
  }

  const isActive = ['preparing', 'ready', 'recording', 'finalizing'].includes(voiceState)

  // Show interim text inside the textarea during active recording
  const displayValue = (() => {
    if (!isActive || !interimText) return value
    const current = value ?? ''
    const sel = selectionRef.current
    if (sel !== null) {
      const before = current.slice(0, sel.start)
      const after = current.slice(sel.end)
      const sep = before && !before.endsWith(' ') ? ' ' : ''
      return before + sep + interimText + after
    }
    return current ? current.trimEnd() + ' ' + interimText : interimText
  })()

  const isSecureCtx = window.isSecureContext
  const supported = isSecureCtx && !!navigator.mediaDevices?.getUserMedia
  const isDisabled = !supported || voiceState === 'preparing' || voiceState === 'finalizing'

  const micBtnClass = [
    `${p}-mic-btn`,
    compact ? 'compact' : '',
    voiceState === 'recording' ? 'listening' : '',
    voiceState === 'preparing' || voiceState === 'finalizing' ? 'initializing' : '',
    voiceState === 'ready' ? 'ready' : '',
    voiceState === 'error' ? 'error' : '',
  ].filter(Boolean).join(' ')

  const micIcon = voiceState === 'preparing' || voiceState === 'finalizing'
    ? <><IconLoader /><span>Attendere...</span></>
    : voiceState === 'ready' || voiceState === 'recording'
    ? <><IconStop /><span>Stop</span></>
    : <><IconMic /><span>Dettatura</span></>

  const micTitle =
    voiceState === 'preparing' ? 'Preparazione microfono...'
    : voiceState === 'ready' ? 'Parla ora — clicca per fermare'
    : voiceState === 'recording' ? 'Ferma registrazione'
    : voiceState === 'finalizing' ? 'Finalizzazione...'
    : voiceState === 'error' ? 'Riprova dettatura'
    : 'Avvia dettatura vocale'

  const barFactors = [0.4, 0.7, 1.0, 0.7, 0.4]
  const showVisualizer = voiceState === 'ready' || voiceState === 'recording'

  return (
    <div className={`${p}-container`}>
      <textarea
        ref={setTextareaRef}
        className={`${p}-textarea`}
        value={displayValue}
        onChange={isActive ? undefined : e => onChange?.(e.target.value)}
        readOnly={isActive}
        placeholder={placeholder}
        rows={rows}
        style={{ minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px` }}
      />

      {supported ? (
        <div className={`${p}-toolbar${compact ? ' compact' : ''}`}>
          <button
            type="button"
            className={micBtnClass}
            onClick={handleMicClick}
            disabled={isDisabled}
            title={micTitle}
            aria-label={micTitle}
          >
            {micIcon}
          </button>

          <div className={`${p}-toolbar-status${compact ? ' compact' : ''}`}>
            {voiceState === 'idle' && !errorMsg && !compact && (
              <>
                <span className={`${p}-info-badge`}>🇮🇹 italiano</span>
                <small style={{ color: '#8c8c8c' }}>{label}</small>
              </>
            )}

            {voiceState === 'preparing' && (
              <span className={`${p}-status-text initializing${compact ? ' compact' : ''}`}>Preparazione microfono...</span>
            )}

            {(voiceState === 'ready' || voiceState === 'recording') && (
              <>
                <span className={`${p}-listening-dot ${voiceState === 'ready' ? 'ready-dot' : ''}`} />
                <span className={`${p}-status-text ${voiceState === 'ready' ? 'ready' : 'listening-active'}${compact ? ' compact' : ''}`}>
                  {voiceState === 'ready' ? 'Parla ora' : 'In ascolto...'}
                </span>
                {showVisualizer && (
                  <div className={`${p}-visualizer${compact ? ' compact' : ''}`}>
                    {barFactors.map((f, i) => (
                      <div
                        key={i}
                        className={`${p}-viz-bar ${voiceState === 'recording' ? 'active' : ''}`}
                        style={{ height: `${Math.max(15, audioLevel * f)}%` }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {voiceState === 'finalizing' && (
              <span className={`${p}-status-text initializing${compact ? ' compact' : ''}`}>Finalizzazione...</span>
            )}

            {errorMsg && (
              <span className={`${p}-status-text error${compact ? ' compact' : ''}`}>{errorMsg}</span>
            )}
          </div>
        </div>
      ) : (
        <div className={`${p}-speech-warning`}>
          {!isSecureCtx
            ? 'Dettatura non disponibile: richiede HTTPS. Contatta l\'amministratore.'
            : 'Speech-to-text non supportato su questo browser. Usa Chrome o Edge moderni.'}
        </div>
      )}
    </div>
  )
})

VoiceTextarea.displayName = 'VoiceTextarea'
export default VoiceTextarea
